import { PropsWithChildren, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../ducks/hooks';
import { updateDownloadFile, markDownloadFileCompleted } from '../../ducks/download-panel/download-panel';
import { TDownloadFileQueue, EDownloadFileStatus } from '../../ducks/download-panel/types-download';
import { useHandleDownloadQueueCadence } from '../../utils/download-queue-utils';

/**
 * ProviderManagerExp: Observes download state and delegates execution and polling to external functions.
 * Uses a single useEffect and a single Map to track the state of all downloads.
 */
const ProviderManagerExp = (props: PropsWithChildren) => {
  const { children } = props;
  const dispatch = useAppDispatch();
  const { handleDownloadQueueCadence } = useHandleDownloadQueueCadence();
  const downloadFiles = useAppSelector((state) => state.downloadManager.downloadFiles);
  const downloadFilesQueue: TDownloadFileQueue[] = useAppSelector((state) => state.downloadManager.downloadFilesQueue ?? []);
  // Single map to track the state of all downloads
  const downloadStatusMap = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    // Combine direct files and queue files into a single array
    const allFiles = [
      ...downloadFiles.map((f) => ({ ...f, type: 'direct' as const })),
      ...downloadFilesQueue.map((f) => ({ ...f, type: 'queue' as const })),
    ];

    allFiles.forEach((file) => {
      const prevStatus = downloadStatusMap.current.get(file.id);
      // Only update the status in the Map if the file is direct
      if (file.type === 'direct' && prevStatus !== file.status) {
        downloadStatusMap.current.set(file.id, file.status);
      }

      // If direct file and status is BEGIN, start download
      if (file.type === 'direct' && file.status === EDownloadFileStatus.BEGIN && prevStatus !== EDownloadFileStatus.BEGIN) {
        downloadStatusMap.current.set(file.id, EDownloadFileStatus.BEGIN);
        void (async () => {
          try {
            await file.apiFunction();
            downloadStatusMap.current.set(file.id, EDownloadFileStatus.COMPLETED);
            dispatch(markDownloadFileCompleted(file.id));
          } catch {
            // Set status to ERROR_DOWNLOAD and update in redux
            downloadStatusMap.current.set(file.id, EDownloadFileStatus.ERROR);
            dispatch(updateDownloadFile({ ...file, status: EDownloadFileStatus.ERROR }));
          }
        })();
      }

      // If file is from queue, delegate to external function
      if (file.type === 'queue') {
        handleDownloadQueueCadence(file, dispatch, downloadStatusMap.current);
      }
    });
  }, [downloadFiles, downloadFilesQueue, dispatch, handleDownloadQueueCadence]);

  return <>{children}</>;
};

export default ProviderManagerExp;
